import { useScienceTutorSection } from './scienceTutorContext';
import { Snowflake } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CrystalObservation } from './SolutionsCrystalObservation';
import { SolubilityGraph } from './SolutionsSolubilityGraph';
import { SOLUBILITY_PRESETS, runCrystalModel } from './solutionsLab';

/** Practice: predict, cool, then calculate the crystal mass; the working appears after Check. */
export function CrystalPractice() {
  const [presetId, setPresetId] = useState(SOLUBILITY_PRESETS[0].id);
  const [hotTemperature, setHotTemperature] = useState(60);
  const [coolTemperature, setCoolTemperature] = useState(20);
  const [mass, setMass] = useState(80);
  const [prediction, setPrediction] = useState<'crystals' | 'no-crystals' | ''>('');
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('CrystalPractice', { presetId, hotTemperature, coolTemperature, mass, prediction, revealed });
  const preset =
    SOLUBILITY_PRESETS.find((candidate) => candidate.id === presetId) ??
    SOLUBILITY_PRESETS[0];
  const model = useMemo(
    () =>
      runCrystalModel({
        preset,
        soluteMassG: mass,
        waterMassG: 100,
        hotTemperatureC: hotTemperature,
        coolTemperatureC: coolTemperature,
      }),
    [coolTemperature, hotTemperature, mass, preset],
  );

  const resetResult = () => {
    setPrediction('');
    setRevealed(false);
  };
  const predictionCorrect = prediction === model.prediction;

  return (
    <article className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
      <div className="grid min-w-0 gap-8 xl:grid-cols-[340px_minmax(0,1fr)_330px]">
        <section className="min-w-0">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
            Build the hot solution
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {SOLUBILITY_PRESETS.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                aria-pressed={candidate.id === preset.id}
                onClick={() => {
                  setPresetId(candidate.id);
                  resetResult();
                }}
                className={`rounded-2xl border p-3 text-left ${
                  candidate.id === preset.id
                    ? 'border-brand-sky bg-wash-sky'
                    : 'border-hairline bg-white'
                }`}
              >
                <span className="block text-sm font-extrabold text-ink">{candidate.formula}</span>
                <span className="mt-1 block text-xs font-medium text-slate2">{candidate.name}</span>
              </button>
            ))}
          </div>

          <label className="mt-6 block text-sm font-extrabold text-ink" htmlFor="hot-temperature">
            Hot temperature <span className="text-brand-coral">{hotTemperature} °C</span>
          </label>
          <input
            id="hot-temperature"
            aria-label="Hot solution temperature"
            type="range"
            min="20"
            max="80"
            step="5"
            value={hotTemperature}
            onChange={(event) => {
              const nextTemperature = Number(event.target.value);
              setHotTemperature(nextTemperature);
              setCoolTemperature((currentTemperature) =>
                Math.min(currentTemperature, Math.max(0, nextTemperature - 5)),
              );
              resetResult();
            }}
            className="mt-2 w-full accent-brand-coral"
          />

          <label className="mt-5 block text-sm font-extrabold text-ink" htmlFor="solute-mass">
            Solute added <span className="text-brand-sky">{mass} g</span>
          </label>
          <input
            id="solute-mass"
            aria-label="Solute mass"
            type="range"
            min="10"
            max="180"
            step="5"
            value={mass}
            onChange={(event) => {
              setMass(Number(event.target.value));
              resetResult();
            }}
            className="mt-2 w-full accent-brand-sky"
          />

          <label className="mt-5 block text-sm font-extrabold text-ink" htmlFor="cool-temperature">
            Cool to <span className="text-brand-mint">{coolTemperature} °C</span>
          </label>
          <input
            id="cool-temperature"
            aria-label="Cooling temperature"
            type="range"
            min="0"
            max={Math.max(0, hotTemperature - 5)}
            step="5"
            value={Math.min(coolTemperature, Math.max(0, hotTemperature - 5))}
            onChange={(event) => {
              setCoolTemperature(Number(event.target.value));
              resetResult();
            }}
            className="mt-2 w-full accent-brand-mint"
          />
          <p className="mt-5 rounded-2xl bg-surface px-4 py-3 text-xs font-medium leading-5 text-slate2">
            100 g water is fixed so the graph and calculation use the same basis.
          </p>
        </section>

        <section className="min-w-0 rounded-[1.75rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.13em] text-brand-sky">
                Solubility curve
              </p>
              <h3 className="mt-1 text-xl font-extrabold text-ink">{preset.name}</h3>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate2">
              {preset.formula}
            </span>
          </div>
          <div className="mt-4 overflow-x-auto">
            <SolubilityGraph
              preset={preset}
              hotTemperature={hotTemperature}
              coolTemperature={coolTemperature}
              loadPer100g={mass}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs font-bold text-slate2">
            <span>● yellow = hot</span>
            <span>● green = cooled</span>
            <span>— curve = maximum dissolved</span>
          </div>
          <p className="mt-4 text-xs font-medium leading-5 text-slate2">{preset.boundary}</p>
        </section>

        <section>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
            Predict the cooling result
          </p>
          <h3 className="mt-2 text-xl font-extrabold text-ink">Will crystals form?</h3>
          <div className="mt-4 grid gap-2">
            <button
              type="button"
              aria-pressed={prediction === 'crystals'}
              disabled={revealed}
              onClick={() => setPrediction('crystals')}
              className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${
                prediction === 'crystals'
                  ? 'border-brand-coral bg-wash-coral'
                  : 'border-hairline bg-white'
              }`}
            >
              Crystals will form
            </button>
            <button
              type="button"
              aria-pressed={prediction === 'no-crystals'}
              disabled={revealed}
              onClick={() => setPrediction('no-crystals')}
              className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold ${
                prediction === 'no-crystals'
                  ? 'border-brand-coral bg-wash-coral'
                  : 'border-hairline bg-white'
              }`}
            >
              No crystals will form
            </button>
          </div>
          <button
            type="button"
            disabled={!prediction || revealed}
            onClick={() => setRevealed(true)}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Snowflake className="h-4 w-4" aria-hidden="true" /> Cool the solution
          </button>

          {revealed ? (
            <div
              role="status"
              className={`mt-5 rounded-2xl border p-4 ${
                predictionCorrect
                  ? 'border-brand-mint/45 bg-wash-mint'
                  : 'border-brand-coral/45 bg-wash-coral'
              }`}
            >
              <p className="font-extrabold text-ink">
                {predictionCorrect ? 'Correct prediction' : 'Check the curve at both temperatures'}
              </p>
              <dl className="mt-3 grid gap-2 text-sm font-medium text-ink-soft">
                <div className="flex justify-between gap-3">
                  <dt>Hot limit</dt>
                  <dd className="font-extrabold">{model.hotLimitG.toFixed(1)} g</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Cool limit</dt>
                  <dd className="font-extrabold">{model.coolLimitG.toFixed(1)} g</dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-white pt-2">
                  <dt>Crystals formed by cooling</dt>
                  <dd className="font-extrabold">{model.crystalsOnCoolingG.toFixed(1)} g</dd>
                </div>
              </dl>
              <p className="mt-3 text-xs font-medium leading-5 text-slate2">
                Cooling lowers the amount that remains dissolved. Any excess leaves the solution as
                solid crystals.
              </p>
            </div>
          ) : null}
        </section>
      </div>
      {revealed ? (
        <CrystalObservation
          preset={preset}
          model={model}
          mass={mass}
          hotTemperature={hotTemperature}
          coolTemperature={coolTemperature}
        />
      ) : null}
    </article>
  );
}
